import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { TeamRole } from './useTeams';

export interface TeamInvitation {
  id: string;
  email: string;
  role: TeamRole;
  invited_by_email: string;
  expires_at: string;
  created_at: string;
}

export interface InvitationPublicInfo {
  team_name: string;
  invited_by_email: string;
  role: TeamRole;
  email: string;
  expires_at: string;
  token?: string;  // Included for user's own pending invitations
}

interface InvitationsListResponse {
  invitations: TeamInvitation[];
}

interface AcceptInvitationResponse {
  message: string;
  team?: {
    id: string;
    name: string;
  };
}

/**
 * Hook for managing team invitations (admin view)
 */
export function useTeamInvitations(teamId: string | null) {
  const queryClient = useQueryClient();

  // List pending invitations for a team
  const invitationsQuery = useQuery({
    queryKey: ['team-invitations', teamId],
    queryFn: async () => {
      if (!teamId) return [];
      const response = await apiClient.get<InvitationsListResponse>(
        `/api/v1/teams/${teamId}/invitations`
      );
      return response.data.invitations;
    },
    enabled: !!teamId,
    staleTime: 1000 * 30, // 30 seconds
  });

  // Revoke an invitation
  const revokeMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!teamId) throw new Error('Team ID is required');
      const response = await apiClient.delete<{ message: string }>(
        `/api/v1/teams/${teamId}/invitations/${invitationId}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invitations', teamId] });
    },
  });

  // Resend an invitation
  const resendMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      if (!teamId) throw new Error('Team ID is required');
      const response = await apiClient.post<{ message: string; invitation: TeamInvitation }>(
        `/api/v1/teams/${teamId}/invitations/${invitationId}/resend`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invitations', teamId] });
    },
  });

  return {
    invitations: invitationsQuery.data ?? [],
    isLoading: invitationsQuery.isLoading,
    error: invitationsQuery.error,
    refetch: invitationsQuery.refetch,

    revokeInvitation: revokeMutation.mutateAsync,
    isRevoking: revokeMutation.isPending,

    resendInvitation: resendMutation.mutateAsync,
    isResending: resendMutation.isPending,
  };
}

/**
 * Hook for getting invitation details by token (public)
 */
export function useInvitationByToken(token: string | null) {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      if (!token) return null;
      const response = await apiClient.get<InvitationPublicInfo>(
        `/api/v1/invitations/${token}`
      );
      return response.data;
    },
    enabled: !!token,
    retry: false, // Don't retry on 404
  });
}

/**
 * Hook for accepting an invitation
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await apiClient.post<AcceptInvitationResponse>(
        `/api/v1/invitations/${token}/accept`
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate teams and user queries after accepting
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      // Invalidate invitations lists (admin view and user's own)
      queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      if (data.team?.id) {
        queryClient.invalidateQueries({ queryKey: ['team-invitations', data.team.id] });
      }
      // Invalidate all team-invitations queries as a fallback
      queryClient.invalidateQueries({ queryKey: ['team-invitations'] });
    },
  });
}

/**
 * Hook for getting pending invitations for the current user
 */
export function useMyInvitations() {
  return useQuery({
    queryKey: ['my-invitations'],
    queryFn: async () => {
      const response = await apiClient.get<{ invitations: InvitationPublicInfo[] }>(
        '/api/v1/invitations'
      );
      return response.data.invitations;
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

// Constants for localStorage
export const PENDING_INVITATION_TOKEN_KEY = 'keyenv_pending_invitation_token';

/**
 * Store a pending invitation token for auto-accept after signup
 */
export function storePendingInvitationToken(token: string) {
  localStorage.setItem(PENDING_INVITATION_TOKEN_KEY, token);
}

/**
 * Get and clear the pending invitation token
 */
export function consumePendingInvitationToken(): string | null {
  const token = localStorage.getItem(PENDING_INVITATION_TOKEN_KEY);
  if (token) {
    localStorage.removeItem(PENDING_INVITATION_TOKEN_KEY);
  }
  return token;
}
